import * as React from "react";
import Box from "@mui/material/Box";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";

import StepButton from "@mui/material/StepButton";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import CodeGenerator from "./CodeGenerator";
import AddUserInformation from "./AddUserInformation";
import AddFoldersToUser from "./AddFoldersToUser";
import UserCreationContextProvider from "./UserCreationContextProvider";
import AddUsergroupsToNewUser from "./AddUsergroupsToNewUser";
import UserCreationContext from "./UserCreationContext";
import NewUserSummary from "./NewUserSummary";
import { Check } from "@mui/icons-material";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import APIWrapperContext from "../../../../services/APIWrapperContext";
import { ApiError, AuthService, UserInviteCreateResult } from "../../../../services/openapi";
import TextField from "@mui/material/TextField";

const getInviteErrorMessage = (error: unknown): string => {
  if (
    error instanceof ApiError &&
    typeof error.body?.detail === "string" &&
    error.body.detail.trim() !== ""
  ) {
    return error.body.detail;
  }

  return "Fehler beim Versenden der Einladung. Bitte versuchen Sie es erneut.";
};

const steps = [
  { name: "Einladung", label: "invite_email", isOwner: undefined },
  {
    name: "User Details anlegen",
    label: "add_user_details",
    isOwner: undefined,
  },
  { name: "Ordner freigeben", label: "assign_folders", isOwner: true },
  {
    name: "Usergruppen hinzufügen",
    label: "assign_usergroups",
    isOwner: false,
  },
  { name: "Zusammenfassung", label: "summary", isOwner: undefined },
];

export default function ConfigureNewUser() {
  const { isOwner, email, resetUserPreConf, buildEnvelope } =
    React.useContext(UserCreationContext);
  const { call_backend_api } = React.useContext(APIWrapperContext);
  const [activeStep, setActiveStep] = React.useState(0);
  const [saving, setSaving] = React.useState(false);
  const [saveError, setSaveError] = React.useState<string | undefined>();
  const [inviteLink, setInviteLink] = React.useState<string | undefined>();
  const [completed, setCompleted] = React.useState<{
    [k: number]: boolean;
  }>({});

  React.useEffect(() => {
    setSaveError(undefined);
  }, [email]);

  const totalSteps = () => {
    return steps.filter((s) => s.isOwner === undefined || s.isOwner === isOwner)
      .length;
  };

  const completedSteps = () => {
    return Object.keys(completed).length;
  };

  const isLastStep = () => {
    return activeStep === totalSteps() - 1;
  };

  const allStepsCompleted = () => {
    return completedSteps() === totalSteps();
  };

  const handleNext = () => {
    const newActiveStep =
      isLastStep() && !allStepsCompleted()
        ? // It's the last step, but not all steps have been completed,
          // find the first step that has been completed
          steps
            .filter((s) => s.isOwner === undefined || s.isOwner === isOwner)
            .findIndex((step, i) => !(i in completed))
        : activeStep + 1;
    setActiveStep(newActiveStep);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleStep = (step: number) => () => {
    setActiveStep(step);
  };

  const handleComplete = () => {
    const newCompleted = completed;
    newCompleted[activeStep] = true;
    setCompleted(newCompleted);
    handleNext();
  };

  const handleSave = () => {
    setSaveError(undefined);
    setSaving(true);
    call_backend_api(
      AuthService.inviteUserAuthRegistrationInvitePost(
        buildEnvelope()
      ),
      (result: UserInviteCreateResult) => {
        setSaving(false);
        setInviteLink(result?.invite_link);
        handleComplete();
      },
      (error: unknown) => {
        setSaving(false);
        setSaveError(getInviteErrorMessage(error));
      },
      undefined,
      false
    );
  };

  const handleReset = () => {
    resetUserPreConf();
    setSaveError(undefined);
    setInviteLink(undefined);
    setActiveStep(0);
    setCompleted({});
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Stepper nonLinear activeStep={activeStep} alternativeLabel>
        {steps
          .filter((s) => s.isOwner === undefined || s.isOwner === isOwner)
          .map((label, index) => (
            <Step key={label.name} completed={completed[index]}>
              <StepButton
                color="inherit" //onClick={handleStep(index)}
              >
                {label.name}
              </StepButton>
            </Step>
          ))}
      </Stepper>
      <div>
        {allStepsCompleted() ? (
          <React.Fragment>
            <Box sx={{ textAlign: "center" }}>
              <Check sx={{ fontSize: "10rem" }} color="success" />
              <Typography sx={{ mt: 2, mb: 1 }}>
                Einladung wurde erfolgreich versendet!
              </Typography>
              {inviteLink && (
                <Box sx={{ mt: 2, textAlign: "left" }}>
                  <Typography variant="body2" color="text.secondary">
                    Aktivierungslink (falls die E-Mail nicht ankommt). Rolle und
                    Ordnerfreigaben werden erst beim Annehmen der Einladung gesetzt.
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
                    <TextField
                      size="small"
                      fullWidth
                      value={inviteLink}
                      InputProps={{ readOnly: true }}
                      onFocus={(e) => e.target.select()}
                    />
                    <Button
                      variant="outlined"
                      onClick={() => {
                        navigator.clipboard?.writeText(inviteLink).catch(() => {});
                      }}
                    >
                      Kopieren
                    </Button>
                  </Box>
                </Box>
              )}
              <Box sx={{ display: "flex", flexDirection: "row", pt: 2 }}>
                <Box sx={{ flex: "1 1 auto" }} />
                <Button onClick={handleReset}>Neue Einladung anlegen</Button>
              </Box>
            </Box>
          </React.Fragment>
        ) : (
          <React.Fragment>
            <Box sx={{ display: "flex", flexDirection: "row", mt: 2 }}>
              <Box sx={{ flex: 1 }} />
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                {steps.filter(
                  (s) => s.isOwner === undefined || s.isOwner === isOwner
                )[activeStep].label === "invite_email" && <CodeGenerator />}
                {steps.filter(
                  (s) => s.isOwner === undefined || s.isOwner === isOwner
                )[activeStep].label === "add_user_details" && (
                  <AddUserInformation />
                )}
                {steps.filter(
                  (s) => s.isOwner === undefined || s.isOwner === isOwner
                )[activeStep].label === "assign_folders" && (
                  <AddFoldersToUser />
                )}
                {steps.filter(
                  (s) => s.isOwner === undefined || s.isOwner === isOwner
                )[activeStep].label === "assign_usergroups" && (
                  <AddUsergroupsToNewUser />
                )}
                {steps.filter(
                  (s) => s.isOwner === undefined || s.isOwner === isOwner
                )[activeStep].label === "summary" && <NewUserSummary />}
              </Box>
              <Box sx={{ flex: 1 }} />
            </Box>
            {saveError && (
              <Alert
                severity="error"
                onClose={() => setSaveError(undefined)}
                sx={{ mt: 2 }}
              >
                {saveError}
              </Alert>
            )}
            <Box sx={{ display: "flex", flexDirection: "row", pt: 2 }}>
              <Button
                color="inherit"
                disabled={activeStep === 0}
                onClick={handleBack}
                sx={{ mr: 1 }}
              >
                Zurück
              </Button>
              <Box sx={{ flex: "1 1 auto" }} />
              {/* <Button onClick={handleNext} sx={{ mr: 1 }}>
                Next
              </Button> */}
              {activeStep !== steps.length && (
                // (completed[activeStep] ? (
                //   <Typography variant="caption" sx={{ display: 'inline-block' }}>
                //     Step {activeStep + 1} already completed
                //   </Typography>
                // ) :
                <Button
                  onClick={(e) => {
                    if (completedSteps() === totalSteps() - 1 && isLastStep()) {
                      handleSave();
                    } else {
                      handleComplete();
                    }
                  }}
                  disabled={email == undefined || email.trim() == "" || saving}
                  title={
                    email == undefined || email.trim() == ""
                      ? "Bitte zuerst eine E-Mail Adresse angeben"
                      : ""
                  }
                >
                  {saving ? <CircularProgress size={20} /> : (completedSteps() === totalSteps() - 1 && isLastStep()
                    ? "Speichern"
                    : "Weiter")}
                </Button>
              )}
            </Box>
          </React.Fragment>
        )}
      </div>
    </Box>
  );
}
